import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { libraryAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import {
    Loader2,
    BookOpen,
    Search,
    Activity,
    GraduationCap,
    Plus,
    ChevronRight,
    Clock,
    ExternalLink,
    Sparkles
} from 'lucide-react';
import Layout from '../components/Layout';

const CATEGORY_ICONS = {
    systems: Activity,
    subjects: GraduationCap
};

export default function Library() {
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const [topics, setTopics] = useState({ by_system: {}, by_subject: {} });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('systems');
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    
    // Request topic modal
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [requestCategory, setRequestCategory] = useState('systems');
    const [requestTopicName, setRequestTopicName] = useState('');
    const [requestDescription, setRequestDescription] = useState('');
    const [submittingRequest, setSubmittingRequest] = useState(false);
    const [generatingBooks, setGeneratingBooks] = useState([]);

    useEffect(() => {
        loadTopics();
        loadHistory();
        loadGenerationStatus();
    }, []);

    // Poll generation status while articles are being generated
    useEffect(() => {
        if (generatingBooks.length === 0) return;
        const interval = setInterval(loadGenerationStatus, 8000);
        return () => clearInterval(interval);
    }, [generatingBooks.length]);

    const loadTopics = async () => {
        try {
            const response = await libraryAPI.getTopics();
            setTopics(response.data);
        } catch (err) {
            console.error('Failed to load topics:', err);
            toast.error('Failed to load library topics');
        } finally {
            setLoading(false);
        }
    };

    const loadHistory = async () => {
        setLoadingHistory(true);
        try {
            const response = await libraryAPI.getHistory();
            setHistory(response.data.history || []);
        } catch (err) {
            console.error('Failed to load history:', err);
        } finally {
            setLoadingHistory(false);
        }
    };

    const loadGenerationStatus = async () => {
        try {
            const response = await libraryAPI.getGenerationStatus();
            const books = response.data.generating || [];
            setGeneratingBooks(books);
            // If generation just completed, refresh topics
            if (generatingBooks.length > 0 && books.length === 0) {
                loadTopics();
            }
        } catch { /* ignore */ }
    };

    const handleTopicClick = (category, systemOrSubject, topic) => {
        navigate(`/library/${category}/${encodeURIComponent(systemOrSubject)}/${encodeURIComponent(topic)}`);
    };

    const handleRequestTopic = async () => {
        if (!requestTopicName.trim()) {
            toast.error('Please enter a topic name');
            return;
        }

        setSubmittingRequest(true);
        try {
            const response = await libraryAPI.requestTopic(
                requestCategory,
                requestTopicName.trim(),
                requestDescription.trim()
            );
            
            if (response.data.existing_content) {
                // Topic already exists - navigate to it
                toast.info('This topic already exists! Taking you there...');
                const content = response.data.existing_content;
                navigate(`/library/${content.category}/${encodeURIComponent(content.system || content.subject)}/${encodeURIComponent(content.topic)}`);
            } else {
                toast.success('Topic request submitted! Content will be generated shortly.');
                // Refresh topics list
                await loadTopics();
            }
            
            setShowRequestModal(false);
            setRequestTopicName('');
            setRequestDescription('');
        } catch (err) {
            console.error('Failed to request topic:', err);
            toast.error(err.response?.data?.detail || 'Failed to submit topic request');
        } finally {
            setSubmittingRequest(false);
        }
    };

    const filterTopics = (topicsObj) => {
        if (!searchTerm) return topicsObj;
        
        const filtered = {};
        Object.entries(topicsObj).forEach(([category, subtopics]) => {
            const matchingTopics = subtopics.filter(topic => 
                topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
                category.toLowerCase().includes(searchTerm.toLowerCase())
            );
            if (matchingTopics.length > 0 || category.toLowerCase().includes(searchTerm.toLowerCase())) {
                filtered[category] = matchingTopics.length > 0 ? matchingTopics : subtopics;
            }
        });
        return filtered;
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
            </Layout>
        );
    }

    const filteredSystems = filterTopics(topics.by_system || {});
    const filteredSubjects = filterTopics(topics.by_subject || {});

    return (
        <Layout>
            <div className="max-w-6xl mx-auto space-y-6 animate-fade-in" data-testid="library-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className={cn(
                            "text-3xl md:text-4xl font-bold mb-2",
                            isDark ? "text-white" : "text-slate-900"
                        )} style={{ fontFamily: 'Manrope' }}>
                            Study Library
                        </h1>
                        <p className={cn(isDark ? "text-slate-300" : "text-slate-600")}>
                            Browse comprehensive study materials by system or subject
                        </p>
                    </div>
                    <Button 
                        onClick={() => setShowRequestModal(true)}
                        className="w-full md:w-auto"
                        data-testid="request-topic-btn"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Request New Topic
                    </Button>
                </div>

                {/* Search */}
                <div className="relative">
                    <Search className={cn(
                        "absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5",
                        isDark ? "text-slate-400" : "text-slate-400"
                    )} />
                    <Input
                        placeholder="Search topics..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={cn(
                            "pl-10",
                            isDark && "bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                        )}
                        data-testid="search-input"
                    />
                </div>

                {/* Auto-generation Progress Banner */}
                {generatingBooks.length > 0 && (
                    <Card className={cn(
                        "border-2 animate-in slide-in-from-top-2 duration-300",
                        isDark ? "bg-indigo-900/30 border-indigo-700" : "bg-indigo-50 border-indigo-200"
                    )} data-testid="generation-progress-banner">
                        <CardContent className="p-4">
                            {generatingBooks.map((book) => {
                                const total = book.auto_gen_total || 0;
                                const completed = book.auto_gen_completed || 0;
                                const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
                                const isExtracting = book.auto_gen_status === 'extracting_topics';
                                return (
                                    <div key={book.id} className="flex items-center gap-3">
                                        <Loader2 className={cn("h-5 w-5 animate-spin flex-shrink-0", isDark ? "text-indigo-400" : "text-indigo-600")} />
                                        <div className="flex-1 min-w-0">
                                            <p className={cn("text-sm font-medium truncate", isDark ? "text-indigo-200" : "text-indigo-800")}>
                                                {isExtracting
                                                    ? `Analyzing "${book.name}" for topics...`
                                                    : `Generating articles from "${book.name}"... ${completed}/${total} complete`}
                                            </p>
                                            {!isExtracting && total > 0 && (
                                                <div className={cn(
                                                    "mt-2 h-1.5 rounded-full overflow-hidden",
                                                    isDark ? "bg-indigo-800" : "bg-indigo-200"
                                                )}>
                                                    <div
                                                        className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </CardContent>
                    </Card>
                )}

                {/* Recent History */}
                {history.length > 0 && !searchTerm && (
                    <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                        <CardHeader className="pb-3">
                            <CardTitle className={cn(
                                "text-lg flex items-center gap-2",
                                isDark ? "text-white" : ""
                            )} style={{ fontFamily: 'Manrope' }}>
                                <Clock className="h-5 w-5 text-indigo-500" />
                                Recently Viewed
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-2">
                                {history.slice(0, 8).map((item, idx) => (
                                    <Badge
                                        key={idx}
                                        variant="secondary"
                                        className={cn(
                                            "cursor-pointer hover:bg-indigo-100 hover:text-indigo-700 transition-colors",
                                            isDark && "bg-slate-700 text-slate-200 hover:bg-indigo-900 hover:text-indigo-300"
                                        )}
                                        onClick={() => navigate(`/library/${item.category}/${encodeURIComponent(item.category === 'systems' ? item.system : item.subject)}/${encodeURIComponent(item.topic)}`)}
                                    >
                                        {item.topic}
                                    </Badge>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Topics Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className={cn(
                        "w-full grid grid-cols-2",
                        isDark ? "bg-slate-800" : "bg-slate-100"
                    )}>
                        <TabsTrigger value="systems" className="flex items-center gap-2" data-testid="tab-systems">
                            <Activity className="h-4 w-4" />
                            By System
                        </TabsTrigger>
                        <TabsTrigger value="subjects" className="flex items-center gap-2" data-testid="tab-subjects">
                            <GraduationCap className="h-4 w-4" />
                            By Subject
                        </TabsTrigger>
                    </TabsList>

                    {/* By System */}
                    <TabsContent value="systems" className="mt-6 space-y-4">
                        {Object.keys(filteredSystems).length === 0 ? (
                            <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                                <CardContent className="p-8 text-center">
                                    <p className={cn(isDark ? "text-slate-400" : "text-slate-500")}>
                                        No matching topics found
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid md:grid-cols-2 gap-4">
                                {Object.entries(filteredSystems).map(([system, subtopics]) => (
                                    <Card 
                                        key={system}
                                        className={cn(
                                            "hover:shadow-lg transition-shadow",
                                            isDark ? "bg-slate-800 border-slate-700" : ""
                                        )}
                                    >
                                        <CardHeader className="pb-3">
                                            <CardTitle className={cn(
                                                "text-lg flex items-center gap-2",
                                                isDark ? "text-white" : ""
                                            )} style={{ fontFamily: 'Manrope' }}>
                                                <Activity className="h-5 w-5 text-indigo-500" />
                                                {system}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2">
                                                {subtopics.map((topic) => (
                                                    <div
                                                        key={topic}
                                                        onClick={() => handleTopicClick('systems', system, topic)}
                                                        className={cn(
                                                            "flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors group",
                                                            isDark 
                                                                ? "bg-slate-900/50 hover:bg-indigo-900/30" 
                                                                : "bg-slate-50 hover:bg-indigo-50"
                                                        )}
                                                        data-testid={`topic-${topic.replace(/\s+/g, '-').toLowerCase()}`}
                                                    >
                                                        <span className={cn(
                                                            "text-sm",
                                                            isDark ? "text-slate-200" : "text-slate-700"
                                                        )}>
                                                            {topic}
                                                        </span>
                                                        <ChevronRight className={cn(
                                                            "h-4 w-4 transition-transform group-hover:translate-x-1",
                                                            isDark ? "text-slate-400" : "text-slate-400"
                                                        )} />
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* By Subject */}
                    <TabsContent value="subjects" className="mt-6 space-y-4">
                        {Object.keys(filteredSubjects).length === 0 ? (
                            <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                                <CardContent className="p-8 text-center">
                                    <p className={cn(isDark ? "text-slate-400" : "text-slate-500")}>
                                        No matching topics found
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid md:grid-cols-2 gap-4">
                                {Object.entries(filteredSubjects).map(([subject, subtopics]) => (
                                    <Card 
                                        key={subject}
                                        className={cn(
                                            "hover:shadow-lg transition-shadow",
                                            isDark ? "bg-slate-800 border-slate-700" : ""
                                        )}
                                    >
                                        <CardHeader className="pb-3">
                                            <CardTitle className={cn(
                                                "text-lg flex items-center gap-2",
                                                isDark ? "text-white" : ""
                                            )} style={{ fontFamily: 'Manrope' }}>
                                                <GraduationCap className="h-5 w-5 text-emerald-500" />
                                                {subject}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2">
                                                {subtopics.map((topic) => (
                                                    <div
                                                        key={topic}
                                                        onClick={() => handleTopicClick('subjects', subject, topic)}
                                                        className={cn(
                                                            "flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors group",
                                                            isDark 
                                                                ? "bg-slate-900/50 hover:bg-emerald-900/30" 
                                                                : "bg-slate-50 hover:bg-emerald-50"
                                                        )}
                                                        data-testid={`topic-${topic.replace(/\s+/g, '-').toLowerCase()}`}
                                                    >
                                                        <span className={cn(
                                                            "text-sm",
                                                            isDark ? "text-slate-200" : "text-slate-700"
                                                        )}>
                                                            {topic}
                                                        </span>
                                                        <ChevronRight className={cn(
                                                            "h-4 w-4 transition-transform group-hover:translate-x-1",
                                                            isDark ? "text-slate-400" : "text-slate-400"
                                                        )} />
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>

                {/* Request Topic Modal */}
                <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
                    <DialogContent className={cn(isDark && "bg-slate-800 border-slate-700")}>
                        <DialogHeader>
                            <DialogTitle className={cn(isDark && "text-white")} style={{ fontFamily: 'Manrope' }}>
                                <span className="flex items-center gap-2">
                                    <Sparkles className="h-5 w-5 text-indigo-500" />
                                    Request New Topic
                                </span>
                            </DialogTitle>
                            <DialogDescription className={cn(isDark && "text-slate-300")}>
                                Request a new study topic. If it already exists, you'll be taken directly to it.
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <label className={cn(
                                    "text-sm font-medium",
                                    isDark ? "text-slate-200" : "text-slate-700"
                                )}>
                                    Category
                                </label>
                                <Select value={requestCategory} onValueChange={setRequestCategory}>
                                    <SelectTrigger className={cn(isDark && "bg-slate-900 border-slate-600")}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="systems">
                                            <span className="flex items-center gap-2">
                                                <Activity className="h-4 w-4" />
                                                By System
                                            </span>
                                        </SelectItem>
                                        <SelectItem value="subjects">
                                            <span className="flex items-center gap-2">
                                                <GraduationCap className="h-4 w-4" />
                                                By Subject
                                            </span>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="space-y-2">
                                <label className={cn(
                                    "text-sm font-medium",
                                    isDark ? "text-slate-200" : "text-slate-700"
                                )}>
                                    Topic Name
                                </label>
                                <Input
                                    placeholder="e.g., Cardiac arrhythmias, Drug interactions..."
                                    value={requestTopicName}
                                    onChange={(e) => setRequestTopicName(e.target.value)}
                                    className={cn(isDark && "bg-slate-900 border-slate-600")}
                                    data-testid="request-topic-name-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <label className={cn(
                                    "text-sm font-medium",
                                    isDark ? "text-slate-200" : "text-slate-700"
                                )}>
                                    Description (Optional)
                                </label>
                                <Textarea
                                    placeholder="Any specific aspects you'd like covered..."
                                    value={requestDescription}
                                    onChange={(e) => setRequestDescription(e.target.value)}
                                    className={cn(isDark && "bg-slate-900 border-slate-600")}
                                    rows={3}
                                />
                            </div>
                        </div>
                        
                        <DialogFooter>
                            <Button 
                                variant="outline" 
                                onClick={() => setShowRequestModal(false)}
                                className={cn(isDark && "border-slate-600")}
                            >
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleRequestTopic}
                                disabled={submittingRequest || !requestTopicName.trim()}
                                data-testid="submit-request-btn"
                            >
                                {submittingRequest ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    'Submit Request'
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
